import React, { useState } from "react";
import moment from "moment/moment";
import { useMutation } from "@apollo/client/react";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import useMediaQuery from "@mui/material/useMediaQuery";
import { serverFormat, momentFormat } from "../../../utils/dates";
import { DELETE_SHIPMENT_MUTATION } from "../../../constants/graphql/mutations";
import { GET_SHIPMENTS_QUERY } from "../../../constants/graphql/queries";
import InboundActions from "./InboundActions";
import InboundRowMobile from "./InboundRowMobile";
import MovewareJobIdCell from "src/components/integrations/moveware/MovewareJobIdCell";

export default function InboundTable({
  list,
  variables,
  onEdit,
  page,
  totalPages,
  totalCount,
  displayCustomerNotified = true,
  showProjectName = false,
  showTotalWeight = false,
  showBillingCharge = false,
  headerAction = null,
  compactActions = false,
  showMovewareDetails = false,
}) {
  const shipmentIdCellSx = {
    maxWidth: 150,
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  };
  const completedByCellSx = {
    ...(compactActions ? { width: "25%", whiteSpace: "nowrap" } : { maxWidth: 220 }),
    overflowWrap: compactActions ? "normal" : "break-word",
    wordBreak: "break-word",
  };

  // const hasDrafts = list.some(item => item.draft);
  // Note: Sorting is now handled by the backend via the order parameter
  const sortedList = list;
  const [selected, setSelected] = useState(null);
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("sm"));
  const [removeShipment, { loading }] = useMutation(DELETE_SHIPMENT_MUTATION, {
    refetchQueries: [{ query: GET_SHIPMENTS_QUERY, variables }],
  });

  const handleRemoveShipment = (id, deleteCorrespondingAssets = false) => {
    return removeShipment({ variables: { id, deleteCorrespondingAssets } });
  };

  const handleRowClick = itemId => {
    setSelected(itemId);
  };

  const formatNotifiedDate = notifiedAt => {
    return moment(notifiedAt, serverFormat).format(momentFormat);
  };

  const renderReceivedDate = item => {
    const timestamp = item.receivedAt || item.completeTime;
    if (!timestamp) return "-";

    const date = moment(timestamp, serverFormat);
    return (
      <Box display="flex" flexDirection="column" alignItems="center">
        <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
          {date.format("ll")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
          {date.format("h:mm A")}
        </Typography>
      </Box>
    );
  };

  const formatTotalWeight = totalWeight => {
    if (totalWeight == null || totalWeight === "") return "-";

    return `${Number(totalWeight).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })} lbs`;
  };

  const formatBillingCharge = item => {
    const total =
      item.billingCharges?.grand_total ??
      item.billingCharges?.grandTotal ??
      item.inboundCharges?.total;

    if (total == null || total === "") return "-";

    return Number(total).toLocaleString(undefined, {
      style: "currency",
      currency: "USD",
    });
  };

  const hasBillingCharge = item => {
    const total =
      item.billingCharges?.grand_total ??
      item.billingCharges?.grandTotal ??
      item.inboundCharges?.total;

    return Number(total || 0) !== 0;
  };
  const renderBillingChargeColumn = showBillingCharge && list.some(hasBillingCharge);

  const hasWeightMismatch = item => {
    const entered = Number(item.totalWeight || 0);
    const calculated = Number(item.calculatedTotalWeight || 0);
    return entered > 0 && calculated > 0 && Math.abs(entered - calculated) > 1;
  };

  return (
    <>
      <Box height={3}>{loading && <LinearProgress />}</Box>
      <Box mb={1} display="flex" justifyContent="space-between" alignItems="center">
        {variables.page && (
          <Typography color="textSecondary" variant="body2">
            {totalCount} Shipments Found. Page {page} of {totalPages}
          </Typography>
        )}
        {headerAction}
      </Box>

      {isMobile ? (
        <Box display="flex" flexDirection="column" gap={1.5}>
          {sortedList.map(item => (
            <InboundRowMobile
              key={`mobile-${item.id}`}
              item={item}
              variables={variables}
              onEdit={onEdit}
              onRemove={deleteCorrespondingAssets =>
                handleRemoveShipment(item.id, deleteCorrespondingAssets)
              }
              displayCustomerNotified={displayCustomerNotified}
              showProjectName={showProjectName}
              showTotalWeight={showTotalWeight}
              showBillingCharge={renderBillingChargeColumn}
              showMovewareDetails={showMovewareDetails}
            />
          ))}
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table
            sx={
              compactActions
                ? {
                    tableLayout: "fixed",
                    width: "100%",
                    "& .MuiTableCell-root": {
                      whiteSpace: "normal",
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                    },
                  }
                : undefined
            }
          >
            <TableHead>
              <TableRow>
                {showProjectName && <TableCell align="center">Project Name</TableCell>}
                <TableCell align="center" sx={shipmentIdCellSx}>
                  Shipment ID
                </TableCell>
                <TableCell align="center">Received On</TableCell>
                <TableCell align="center" sx={completedByCellSx}>
                  Completed By
                </TableCell>
                {showMovewareDetails && <TableCell align="center">Moveware Details</TableCell>}
                {showTotalWeight && <TableCell align="center">Inbound Weight</TableCell>}
                {renderBillingChargeColumn && <TableCell align="center">Billing Charge</TableCell>}
                {/* hasDrafts && <TableCell>Status</TableCell> */}
                {displayCustomerNotified && <TableCell align="center">Customer Notified</TableCell>}
                <TableCell
                  align="center"
                  sx={compactActions ? { width: 64, px: 0.5, whiteSpace: "nowrap" } : undefined}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedList.map(item => (
                <TableRow
                  key={item.id}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  hover
                  selected={selected === item.id}
                  onClick={() => handleRowClick(item.id)}
                >
                  {showProjectName && (
                    <TableCell align="center" sx={{ maxWidth: 150 }}>
                      {item.project?.name || "-"}
                    </TableCell>
                  )}
                  <TableCell
                    align="center"
                    sx={{ ...shipmentIdCellSx, color: item.draft ? "red" : "inherit" }}
                  >
                    {item.shipmentId}
                    {item.revisionNumber > 0 ? `.R${item.revisionNumber}` : ""}
                  </TableCell>
                  <TableCell align="center">
                    {renderReceivedDate(item)}
                  </TableCell>
                  <TableCell align="center" sx={completedByCellSx}>
                    {item.completer?.email || item.user.email}
                  </TableCell>
                  {showMovewareDetails && (
                    <TableCell align="center">
                      <MovewareJobIdCell item={item} />
                    </TableCell>
                  )}
                  {showTotalWeight && (
                    <TableCell align="center">
                      <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                        {formatTotalWeight(item.totalWeight)}
                        {hasWeightMismatch(item) && (
                          <WarningAmberIcon color="warning" fontSize="small" titleAccess="Weight mismatch" />
                        )}
                      </Box>
                    </TableCell>
                  )}
                  {renderBillingChargeColumn && (
                    <TableCell align="center">{formatBillingCharge(item)}</TableCell>
                  )}
                  {/* hasDrafts && (
                    <TableCell>
                      <Box sx={{ color: item.draft ? "red" : "inherit", fontWeight: 500 }}>
                        {item.draft ? "Draft" : "Complete"}
                      </Box>
                    </TableCell> 
                  )} */}
                  {displayCustomerNotified && (
                    <TableCell align="center">
                      {item.notifiedAt ? (
                        <>
                          <Box align="center">Notified On</Box>
                          <Box align="center">{formatNotifiedDate(item.notifiedAt)}</Box>
                        </>
                      ) : (
                        <Box align="center">Pending</Box>
                      )}
                    </TableCell>
                  )}
                  <TableCell
                    align="center"
                    sx={compactActions ? { width: 64, px: 0.5 } : undefined}
                  >
                    <Box
                      display="flex"
                      flexDirection={compactActions ? "column" : "row"}
                      alignItems="center"
                    >
                      <InboundActions
                        item={item}
                        onEdit={onEdit}
                        onRemove={deleteCorrespondingAssets =>
                          handleRemoveShipment(item.id, deleteCorrespondingAssets)
                        }
                        variables={variables}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
}
